import { ChevronDown, ChevronRight } from "lucide-react";
import React from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { CountChip } from "./CountChip";
import { SECTION_DRAG_TYPE } from "./SectionDragHandle";

export const CollapsibleSection = ({
	storageKey,
	title,
	count,
	countTooltip,
	icon: Icon,
	iconColor,
	children,
	dragHandle,
	dragId,
	dragLabel,
	dropEdge = "before",
	headerActions,
	isDragging = false,
	isDropTarget = false,
	forceCollapsed = false,
	onSectionDragStart,
	onSectionDragEnd,
	onDragOver,
	onDrop,
	selectionBar,
	style,
	defaultCollapsed = false,
	collapsed,
	onCollapsedChange,
	variant = "default",
}) => {
	const [isCollapsed, setIsCollapsed] = useLocalStorage(
		storageKey,
		defaultCollapsed,
	);
	const isRepoStyle = variant === "repo";
	const isControlled = typeof collapsed === "boolean";
	const baseCollapsed = isControlled ? collapsed : isCollapsed;
	const effectiveCollapsed = forceCollapsed || baseCollapsed;
	const sectionRef = React.useRef(null);
	const [shouldRenderBody, setShouldRenderBody] = React.useState(
		!effectiveCollapsed,
	);

	const scrollSectionIntoView = React.useCallback(() => {
		const section = sectionRef.current;
		if (!section || typeof window === "undefined") return;

		const prefersReducedMotion = window.matchMedia?.(
			"(prefers-reduced-motion: reduce)",
		).matches;
		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				section.scrollIntoView({
					block: "start",
					behavior: prefersReducedMotion ? "auto" : "smooth",
				});
			});
		});
	}, []);

	React.useEffect(() => {
		if (!effectiveCollapsed) {
			setShouldRenderBody(true);
			return undefined;
		}

		const timer = setTimeout(() => {
			setShouldRenderBody(false);
		}, 100);
		return () => clearTimeout(timer);
	}, [effectiveCollapsed]);

	const toggle = () => {
		const nextCollapsed = !baseCollapsed;
		if (!isControlled) setIsCollapsed(nextCollapsed);
		onCollapsedChange?.(nextCollapsed);
		if (!nextCollapsed) scrollSectionIntoView();
	};
	const hasActionRow = !effectiveCollapsed && (headerActions || selectionBar);
	const shouldRenderActionRow =
		shouldRenderBody && (headerActions || selectionBar);
	const canDragSection = Boolean(dragId);
	const sectionStateClassName = `${isDropTarget ? "is-drop-target" : ""} ${
		isDragging ? "is-dragging scale-[0.995] opacity-70" : ""
	}`;
	const sectionClassName = isRepoStyle
		? `section-raised-row relative mb-3 scroll-mt-4 transition-[background-color,border-color,box-shadow,opacity,transform] duration-150 ${sectionStateClassName}`
		: `section-raised-row relative mb-4 scroll-mt-4 p-3 transition-[background-color,border-color,box-shadow,opacity,transform] duration-150 ${sectionStateClassName}`;
	const dropMarkerClassName = `pointer-events-none absolute left-0 right-0 z-20 h-0.5 rounded-full bg-[var(--color-accent)] ${
		dropEdge === "after"
			? isRepoStyle
				? "-bottom-2"
				: "-bottom-3"
			: isRepoStyle
				? "-top-2"
				: "-top-3"
	}`;
	const headerClassName = isRepoStyle
		? `section-raised-header relative flex select-none flex-wrap items-center justify-between gap-3 px-3 py-2 transition-colors ${canDragSection ? "cursor-grab active:cursor-grabbing" : ""}`
		: `section-raised-header relative -mx-2 flex select-none items-center px-2 py-1.5 transition-colors ${canDragSection ? "cursor-grab active:cursor-grabbing" : ""} ${hasActionRow ? "mb-3" : effectiveCollapsed ? "" : "mb-4"}`;
	const headerContentClassName = isRepoStyle
		? "pointer-events-none relative z-10 flex min-w-0 flex-1 items-center gap-1"
		: "pointer-events-none relative z-10 flex min-w-0 items-center gap-1";
	const titleClassName = isRepoStyle
		? "flex min-w-0 flex-1 items-center gap-2 text-left text-[var(--color-text-primary)]"
		: "flex min-w-0 items-center gap-2 text-left text-base font-semibold text-[var(--color-text-primary)]";
	const titleTextClassName = isRepoStyle
		? "min-w-0 truncate text-sm font-semibold"
		: "min-w-0";
	const actionRowClassName = isRepoStyle
		? "mb-3 flex min-w-0 flex-wrap items-center justify-between gap-3"
		: "mb-3 flex min-w-0 flex-nowrap items-center gap-2 overflow-visible";
	const bodyInnerClassName = isRepoStyle
		? "border-t border-[var(--color-section-raised-border)] px-3 py-3"
		: "";

	const handleSectionDragStart = (event) => {
		if (!dragId || !sectionRef.current) return;

		event.stopPropagation();
		const rect = sectionRef.current.getBoundingClientRect();
		const preview = sectionRef.current.cloneNode(true);
		preview.style.position = "fixed";
		preview.style.left = "-10000px";
		preview.style.top = "-10000px";
		preview.style.width = `${rect.width}px`;
		preview.style.maxHeight = "144px";
		preview.style.overflow = "hidden";
		preview.style.pointerEvents = "none";
		preview.style.opacity = "0.96";
		preview.style.transform = "scale(0.985)";
		preview.style.boxShadow = "var(--shadow-popover)";
		document.body.appendChild(preview);

		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData(SECTION_DRAG_TYPE, dragId);
		event.dataTransfer.setData("text/plain", `section:${dragId}`);
		event.dataTransfer.setDragImage(
			preview,
			Math.min(Math.max(event.clientX - rect.left, 24), rect.width - 24),
			Math.min(Math.max(event.clientY - rect.top, 24), 72),
		);

		window.setTimeout(() => preview.remove(), 0);
		onSectionDragStart?.(dragId);
	};

	const handleSectionDragEnd = (event) => {
		event.stopPropagation();
		onSectionDragEnd?.();
	};

	return (
		<div
			ref={sectionRef}
			className={sectionClassName}
			onDragOver={onDragOver}
			onDrop={onDrop}
			style={style}
		>
			{isDropTarget && <div className={dropMarkerClassName} />}
			<div
				className={headerClassName}
				draggable={canDragSection}
				onDragStart={handleSectionDragStart}
				onDragEnd={handleSectionDragEnd}
			>
				<button
					type="button"
					draggable={canDragSection}
					aria-expanded={!effectiveCollapsed}
					aria-label={
						effectiveCollapsed ? "Expand section" : "Collapse section"
					}
					title={canDragSection ? `Drag to reorder ${dragLabel}` : undefined}
					onClick={toggle}
					onDragStart={handleSectionDragStart}
					onDragEnd={handleSectionDragEnd}
					className="no-hover-lift absolute inset-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
				/>
				<div className={headerContentClassName}>
					{count !== undefined && (
						<CountChip count={count} tooltip={countTooltip} />
					)}
					<span>{dragHandle}</span>
					<span className={titleClassName}>
						{effectiveCollapsed ? (
							<ChevronRight className="w-4 h-4 shrink-0 text-[var(--color-text-secondary)]" />
						) : (
							<ChevronDown className="w-4 h-4 shrink-0 text-[var(--color-text-secondary)]" />
						)}
						{Icon && <Icon className={`w-4 h-4 shrink-0 ${iconColor}`} />}
						<span className={titleTextClassName}>{title}</span>
					</span>
				</div>
			</div>

			{shouldRenderBody && (
				<div
					className={`grid transition-[grid-template-rows,opacity] duration-100 ease-out ${
						effectiveCollapsed
							? "grid-rows-[0fr] opacity-0"
							: "grid-rows-[1fr] opacity-100"
					}`}
				>
					<div
						className={`min-h-0 overflow-hidden transition-transform duration-100 ease-out ${
							effectiveCollapsed ? "-translate-y-1" : "translate-y-0"
						}`}
					>
						<div className={bodyInnerClassName}>
							{shouldRenderActionRow && (
								<div className={actionRowClassName}>
									{headerActions && (
										<div className="relative z-10 min-w-0 flex-1">
											{headerActions}
										</div>
									)}
									{selectionBar && (
										<div className="relative z-10 ml-auto shrink-0">
											{selectionBar}
										</div>
									)}
								</div>
							)}

							{children}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CollapsibleSection;
